// Sincronización con el DENUE (PRD J.1–J.4): panel en IA y ajustes y "Actualizar con DENUE" en la ficha.
// El trabajo lo hace el servidor (scripts/denue_sync.py); aquí solo se pide y se muestra.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { ESTADOS } from "../directory/directoryFilters";
import { useOffers } from "../useOffers";
import { useIsDueno } from "../useIsDueno";

const sb = () => getSupabaseClient();
const nf = (n?: number | null) => (n ?? 0).toLocaleString("es-MX");
const fecha = (s?: string | null) =>
  s
    ? new Date(s).toLocaleString("es-MX", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

type Run = {
  id: number;
  kind: "revisar" | "simular" | "aplicar";
  phase: string;
  corte: string | null;
  estados: string[] | null;
  started_at: string;
  finished_at: string | null;
  added: number;
  updated: number;
  closed: number;
  notes: string | null;
  summary: Summary | null;
  progress: {
    estado?: string;
    hechos?: number;
    total?: number;
    paso?: string;
  } | null;
  sim_run: number | null;
  baseline: boolean;
  backup_file: string | null;
  pause_requested: boolean;
};
type PorEstado = {
  estado: string;
  filas: number;
  nuevas: number;
  actualizar: number;
  empate: number;
  omitidas: number;
  dudosas: number;
  cerrar: number;
};
type Summary = {
  nuevas: number;
  actualizar: number;
  cerrar: number;
  empate: number;
  dudosas: number;
  cerrar_con_oportunidad: number;
  publicado?: string;
  por_estado: PorEstado[];
  por_producto: Record<string, number>;
  por_producto_qro: Record<string, number>;
  ejemplos_encajan: {
    nombre: string;
    municipio: string;
    estado: string;
    fits: string[];
  }[];
  ejemplos: {
    nuevas: {
      nombre: string;
      municipio: string;
      estado: string;
      actividad: string;
    }[];
    actualizar: {
      nombre: string;
      municipio: string;
      campos: string[];
      antes: Record<string, string | null>;
      despues: Record<string, string | null>;
    }[];
    cerrar: {
      nombre: string;
      municipio: string;
      estado: string;
      en_crm: boolean;
    }[];
    dudosas: { nombre: string; municipio: string; estado: string }[];
  };
};
type Status = {
  ajustes: {
    corte: string;
    publicado: string;
    day: number;
    hora: string;
    auto: boolean;
    add_new: boolean;
    update: boolean;
    keep_mine: boolean;
    mark_closed: boolean;
    notify: boolean;
    ultima_revision?: string;
    disponible?: string;
  };
  total: number;
  cerradas: number;
  empatadas: number;
  corridas: Run[];
};

const BUSY = [
  "en_cola",
  "descargando",
  "comparando",
  "respaldando",
  "aplicando",
];
const PHASE: Record<string, string> = {
  en_cola: "En cola: empieza en menos de un minuto",
  descargando: "Descargando del INEGI",
  comparando: "Comparando con tu directorio",
  simulada: "Simulación lista",
  respaldando: "Haciendo el respaldo completo",
  aplicando: "Aplicando en lotes de 50 mil",
  pausada: "Pausada",
  terminada: "Terminada",
  sin_corte_nuevo: "Sin corte nuevo",
  corte_nuevo: "¡Hay corte nuevo!",
  error: "Error",
  cancelada: "Cancelada",
};
const KIND: Record<Run["kind"], string> = {
  revisar: "Revisión",
  simular: "Simulación",
  aplicar: "Aplicación",
};

const useDenue = () =>
  useQuery({
    queryKey: ["denue_status"],
    queryFn: async () => {
      const { data, error } = await sb().rpc("denue_status");
      if (error) throw error;
      return data as Status;
    },
    refetchInterval: (q) =>
      (q.state.data as Status | undefined)?.corridas.some((r) =>
        BUSY.includes(r.phase),
      )
        ? 4000
        : false,
  });

/** Próxima revisión automática: día N del mes a la hora guardada (después del respaldo de las 03:00). */
const nextCheck = (day: number, hora: string) => {
  const now = new Date();
  const [h, m] = hora.split(":").map(Number);
  let d = new Date(now.getFullYear(), now.getMonth(), day, h, m);
  if (d <= now) d = new Date(now.getFullYear(), now.getMonth() + 1, day, h, m);
  return d.toLocaleDateString("es-MX");
};

const Stat = ({
  cap,
  v,
  sub,
}: {
  cap: string;
  v: React.ReactNode;
  sub?: React.ReactNode;
}) => (
  <div className="flex flex-col gap-0.5 rounded-xl border p-3">
    <span className="text-xs text-muted-foreground">{cap}</span>
    <span className="text-xl font-extrabold">{v}</span>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
  </div>
);

export const DenueSection = () => {
  const { data: s, refetch } = useDenue();
  const notify = useNotify();
  const qc = useQueryClient();
  const [pick, setPick] = useState<string[]>(["Querétaro"]);
  const [confirm, setConfirm] = useState<Run | null>(null);
  const [busy, setBusy] = useState(false);
  if (!s) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const A = s.ajustes;
  const active = s.corridas.find(
    (r) => BUSY.includes(r.phase) || r.phase === "pausada",
  );
  const lastSim = s.corridas.find(
    (r) => r.kind === "simular" && r.phase === "simulada",
  );
  const applied =
    lastSim &&
    s.corridas.some((r) => r.sim_run === lastSim.id && r.phase !== "error");
  const lastCheck = s.corridas.find(
    (r) => r.kind === "revisar" && !BUSY.includes(r.phase),
  );

  const call = async (
    fn: string,
    args: Record<string, unknown>,
    ok: string,
  ) => {
    setBusy(true);
    const { error } = await sb().rpc(fn, args);
    setBusy(false);
    if (error) return notify(error.message, { type: "error" });
    notify(ok, { type: "success" });
    qc.invalidateQueries({ queryKey: ["denue_status"] });
    refetch();
  };
  const saveOpt = async (k: string, v: boolean) => {
    const { data, error } = await sb()
      .from("crm_settings")
      .update({ denue: { ...A, [k]: v } })
      .eq("id", 1)
      .select("id");
    if (error || !data?.length)
      return notify("Solo un administrador puede cambiar esto", {
        type: "error",
      });
    refetch();
  };
  const toggle = (e: string) =>
    setPick((p) => (p.includes(e) ? p.filter((x) => x !== e) : [...p, e]));
  const estados = ["Querétaro", ...ESTADOS.filter((e) => e !== "Querétaro")];

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-muted-foreground">
        Tu directorio es el corte {A.corte} del DENUE (INEGI). Cada mes el CRM
        revisa si el INEGI publicó uno nuevo. Para aplicarlo: primero{" "}
        <b>Simular</b> (descarga y compara, sin cambiar nada) y luego{" "}
        <b>Aplicar</b> (con respaldo antes, en lotes de 50 mil y con opción de
        pausar). Nunca se borra ninguna empresa.
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          cap="Corte actual"
          v={A.corte}
          sub={`${nf(s.total)} empresas · publicado ${A.publicado}`}
        />
        <Stat
          cap="Última revisión"
          v={
            lastCheck
              ? fecha(lastCheck.finished_at ?? lastCheck.started_at)
              : "—"
          }
          sub={lastCheck ? PHASE[lastCheck.phase] : "Todavía no se revisa"}
        />
        <Stat
          cap="Próxima revisión"
          v={A.auto ? nextCheck(A.day, A.hora) : "apagada"}
          sub={`día ${A.day} de cada mes, ${A.hora} (después del respaldo de las 03:00)`}
        />
        <Stat
          cap="Con clave DENUE"
          v={nf(s.empatadas)}
          sub={`${nf(s.cerradas)} marcadas cerradas`}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={busy || !!active}
          onClick={() =>
            call(
              "denue_request",
              { p_kind: "revisar" },
              "Revisando: tarda menos de un minuto",
            )
          }
        >
          <RefreshCw /> Revisar ahora
        </Button>
      </div>

      <div className="grid gap-1.5 sm:grid-cols-2">
        {(
          [
            ["auto", "Revisar cada mes automáticamente"],
            ["add_new", "Agregar empresas nuevas"],
            ["update", "Actualizar teléfono, correo, sitio y dirección"],
            [
              "keep_mine",
              "Nunca sobrescribir datos que tú o la IA corrigieron",
            ],
            ["mark_closed", "Marcar las que cerraron (sin borrarlas del CRM)"],
            [
              "notify",
              "Avisarme de empresas nuevas que encajan en mis productos",
            ],
          ] as const
        ).map(([k, l]) => (
          <label key={k} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!A[k]}
              disabled={k === "keep_mine"}
              onChange={(e) => saveOpt(k, e.target.checked)}
            />
            {l}
          </label>
        ))}
      </div>

      <div className="flex flex-col gap-2 rounded-xl border p-3">
        <b>Simular un corte</b>
        <span className="text-xs text-muted-foreground">
          Elige estados (Querétaro primero). Descarga el archivo oficial de cada
          uno, lo compara y te dice qué pasaría, sin cambiar nada. Los archivos
          se borran al terminar. Un estado chico tarda segundos; todo el país,
          unos 30 minutos.
        </span>
        <div className="flex flex-wrap gap-1.5">
          {estados.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => toggle(e)}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-xs",
                pick.includes(e) &&
                  "border-primary bg-primary text-primary-foreground",
              )}
            >
              {e}
            </button>
          ))}
          <button
            type="button"
            className="rounded-full border px-2.5 py-0.5 text-xs font-semibold"
            onClick={() =>
              setPick(pick.length === estados.length ? ["Querétaro"] : estados)
            }
          >
            {pick.length === estados.length ? "Solo Querétaro" : "Todo el país"}
          </button>
        </div>
        <div>
          <Button
            disabled={busy || !!active || !pick.length}
            onClick={() =>
              call(
                "denue_request",
                { p_kind: "simular", p_estados: pick },
                "Simulación en cola",
              )
            }
          >
            Simular (
            {pick.length === estados.length
              ? "todo el país"
              : `${pick.length} estado${pick.length === 1 ? "" : "s"}`}
            )
          </Button>
        </div>
      </div>

      {active && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary p-3">
          <b>
            {KIND[active.kind]} · {PHASE[active.phase]}
          </b>
          {active.progress?.total ? (
            <span className="text-muted-foreground">
              {active.progress.estado ? `${active.progress.estado} · ` : ""}
              {nf(active.progress.hechos)} de {nf(active.progress.total)}
            </span>
          ) : null}
          {active.kind === "aplicar" && active.phase !== "pausada" && (
            <Button
              size="sm"
              variant="outline"
              disabled={active.pause_requested}
              onClick={() =>
                call(
                  "denue_pause",
                  { p_run: active.id, p_pause: true },
                  "Se pausa al terminar el lote en curso",
                )
              }
            >
              {active.pause_requested ? "Pausando…" : "Pausar"}
            </Button>
          )}
          {active.phase === "pausada" && (
            <Button
              size="sm"
              onClick={() =>
                call(
                  "denue_pause",
                  { p_run: active.id, p_pause: false },
                  "Reanudando",
                )
              }
            >
              Reanudar
            </Button>
          )}
        </div>
      )}

      {lastSim?.summary && (
        <SimResult
          run={lastSim}
          applied={!!applied}
          canApply={!active && !applied}
          onApply={() => setConfirm(lastSim)}
        />
      )}

      <History runs={s.corridas} />
      <span className="text-xs text-muted-foreground">
        Cómo empata: por la clave del DENUE; si no la tiene, por coordenadas y
        actividad, y luego por nombre normalizado + código postal + actividad
        (solo si el empate es de uno a uno; los casos dudosos no se aplican:
        quedan en la lista para que los revises). Una empresa se marca “cerrada”
        solo si no empata ni por clave ni por nombre; si tiene oportunidades
        abiertas no se cambian: te avisa en Mi día.
      </span>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Aplicar el corte {confirm?.corte}?</DialogTitle>
            <DialogDescription>
              {confirm?.estados?.join(", ")}. Primero se hace un respaldo
              completo de la base (2 a 3 minutos). Luego se aplica en lotes de
              50 mil; el CRM sigue funcionando y puedes pausarlo.
            </DialogDescription>
          </DialogHeader>
          {confirm?.summary && (
            <ul className="list-disc pl-5 text-sm">
              {confirm.baseline ? (
                <li>
                  Empate inicial: guarda la clave DENUE de{" "}
                  {nf(confirm.summary.empate)} empresas. No cambia ningún dato.
                </li>
              ) : (
                <>
                  <li>{nf(confirm.summary.nuevas)} empresas nuevas</li>
                  <li>
                    {nf(confirm.summary.actualizar)} con datos actualizados
                    (nunca los que corregiste)
                  </li>
                  <li>
                    {nf(confirm.summary.cerrar)} marcadas como cerradas (no se
                    borran)
                  </li>
                </>
              )}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancelar
            </Button>
            <Button
              disabled={busy}
              onClick={async () => {
                await call(
                  "denue_request",
                  { p_kind: "aplicar", p_sim: confirm!.id },
                  "En cola: primero el respaldo",
                );
                setConfirm(null);
              }}
            >
              Sí, aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SimResult = ({
  run,
  applied,
  canApply,
  onApply,
}: {
  run: Run;
  applied: boolean;
  canApply: boolean;
  onApply: () => void;
}) => {
  const { data: offers } = useOffers();
  const S = run.summary!;
  const name = (slug: string) =>
    offers?.find((o) => o.slug === slug)?.name ?? slug;
  const prods = (m: Record<string, number>) =>
    Object.entries(m).length
      ? Object.entries(m)
          .sort((a, b) => b[1] - a[1])
          .map(([k, v]) => `${name(k)} ${nf(v)}`)
          .join(" · ")
      : "ninguna";
  return (
    <div className="flex flex-col gap-2 rounded-xl border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <b>
          Simulación del corte {run.corte} · {run.estados?.join(", ")}
        </b>
        <span className="text-xs text-muted-foreground">
          {fecha(run.finished_at)}
        </span>
        <span className="flex-1" />
        {applied ? (
          <span className="text-xs font-semibold">Ya se aplicó</span>
        ) : (
          <Button size="sm" disabled={!canApply} onClick={onApply}>
            Aplicar…
          </Button>
        )}
      </div>
      {run.baseline && (
        <p className="text-xs text-muted-foreground">
          Es el mismo corte que tu directorio: se hace el <b>empate inicial</b>.
          Solo se guarda la clave DENUE de cada empresa y una huella de sus
          datos, para que en los cortes siguientes el CRM sepa exactamente qué
          cambió. Las empresas del DENUE que no están en tu directorio (la carga
          original dejó fuera las que no tenían contacto y algunas repetidas o
          de gobierno) quedan como “omitidas” y nunca se cuentan como nuevas.
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-4">
        <Stat cap="Nuevas" v={nf(S.nuevas)} />
        <Stat cap="Se actualizarían" v={nf(S.actualizar)} />
        <Stat
          cap="Se marcarían cerradas"
          v={nf(S.cerrar)}
          sub={
            S.cerrar_con_oportunidad
              ? `${nf(S.cerrar_con_oportunidad)} están en tu CRM`
              : undefined
          }
        />
        <Stat
          cap={
            run.baseline ? "Empatadas (clave DENUE)" : "Dudosas (no se aplican)"
          }
          v={nf(run.baseline ? S.empate : S.dudosas)}
        />
      </div>
      <p>
        <b>Nuevas que encajan en tus productos</b> · Querétaro:{" "}
        {prods(S.por_producto_qro)} · Todas: {prods(S.por_producto)}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1 pr-2">Estado</th>
              <th className="pr-2">Filas del DENUE</th>
              <th className="pr-2">Nuevas</th>
              <th className="pr-2">Actualizar</th>
              <th className="pr-2">Cerrar</th>
              <th className="pr-2">Empatadas</th>
              <th className="pr-2">Omitidas</th>
              <th>Dudosas</th>
            </tr>
          </thead>
          <tbody>
            {S.por_estado.map((e) => (
              <tr key={e.estado} className="border-t">
                <td className="py-1 pr-2 font-semibold">{e.estado}</td>
                <td className="pr-2">{nf(e.filas)}</td>
                <td className="pr-2">{nf(e.nuevas)}</td>
                <td className="pr-2">{nf(e.actualizar)}</td>
                <td className="pr-2">{nf(e.cerrar)}</td>
                <td className="pr-2">{nf(e.empate)}</td>
                <td className="pr-2">{nf(e.omitidas)}</td>
                <td>{nf(e.dudosas)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Examples
        title="Ejemplos de nuevas"
        rows={S.ejemplos.nuevas.map(
          (x) => `${x.nombre} · ${x.municipio}, ${x.estado} · ${x.actividad}`,
        )}
      />
      <Examples
        title="Nuevas que encajan"
        rows={S.ejemplos_encajan.map(
          (x) =>
            `${x.nombre} · ${x.municipio}, ${x.estado} · ${x.fits.map(name).join(", ")}`,
        )}
      />
      <Examples
        title="Ejemplos de actualizaciones"
        rows={S.ejemplos.actualizar.map(
          (x) =>
            `${x.nombre} (${x.municipio}): ${x.campos.map((c) => `${c} «${x.antes[c] ?? "—"}» → «${x.despues[c] ?? "—"}»`).join("; ")}`,
        )}
      />
      <Examples
        title="Ejemplos de cerradas"
        rows={S.ejemplos.cerrar.map(
          (x) =>
            `${x.nombre} · ${x.municipio}, ${x.estado}${x.en_crm ? " · está en tu CRM" : ""}`,
        )}
      />
      <Examples
        title="Dudosas (no se aplican: revísalas)"
        rows={S.ejemplos.dudosas.map(
          (x) => `${x.nombre} · ${x.municipio}, ${x.estado}`,
        )}
      />
    </div>
  );
};

const Examples = ({ title, rows }: { title: string; rows: string[] }) =>
  rows.length ? (
    <details className="text-xs">
      <summary className="cursor-pointer font-semibold">
        {title} ({rows.length})
      </summary>
      <ul className="mt-1 list-disc pl-5">
        {rows.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
    </details>
  ) : null;

const History = ({ runs }: { runs: Run[] }) =>
  runs.length ? (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-2">Fecha</th>
            <th className="pr-2">Qué</th>
            <th className="pr-2">Resultado</th>
            <th className="pr-2">Nuevas</th>
            <th className="pr-2">Actualizadas</th>
            <th>Cerradas</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} className="border-t align-top">
              <td className="py-1 pr-2 whitespace-nowrap">
                {fecha(r.started_at)}
              </td>
              <td className="pr-2">
                {KIND[r.kind]}
                {r.estados?.length
                  ? ` · ${r.estados.length > 3 ? `${r.estados.length} estados` : r.estados.join(", ")}`
                  : ""}
              </td>
              <td className="pr-2">
                {PHASE[r.phase] ?? r.phase}
                {r.notes && (
                  <div className="text-muted-foreground">{r.notes}</div>
                )}
              </td>
              <td className="pr-2">
                {r.kind === "aplicar" ? nf(r.added) : "—"}
              </td>
              <td className="pr-2">
                {r.kind === "aplicar" ? nf(r.updated) : "—"}
              </td>
              <td>{r.kind === "aplicar" ? nf(r.closed) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="text-xs text-muted-foreground">Todavía no hay historial.</p>
  );

/* ------------------------------------------------ ficha: Actualizar con DENUE (J.3) */
type Change = {
  campo: string;
  actual: string | null;
  denue: string | null;
  editado: boolean;
};
const LABEL: Record<string, string> = {
  telefono: "Teléfono",
  email: "Correo",
  sitio_web: "Sitio web",
  direccion: "Dirección",
};

interface DenueUpdateProps {
  dirId: number;
  onDone: () => void;
}

/** Only the owner updates a record from the DENUE (denue_apply_fields is owner-only). */
export const DenueUpdateButton = (props: DenueUpdateProps) =>
  useIsDueno() ? <DenueUpdateDialog {...props} /> : null;

const DenueUpdateDialog = ({ dirId, onDone }: DenueUpdateProps) => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<{
    found: { denue_id: string; nombre: string } | null;
    changes: Change[];
  } | null>(null);
  const [sel, setSel] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const look = async () => {
    setOpen(true);
    setLoading(true);
    setErr(null);
    setRes(null);
    const { data, error } = await sb().functions.invoke("ai", {
      body: { action: "denue_lookup", directory_id: dirId },
    });
    setLoading(false);
    if (error) {
      const msg = await (error as { context?: Response }).context
        ?.json?.()
        .catch(() => null);
      return setErr(msg?.message ?? error.message);
    }
    setRes(data);
    setSel(
      (data.changes as Change[]).filter((c) => !c.editado).map((c) => c.campo),
    );
  };
  const save = async () => {
    const fields = Object.fromEntries(
      res!.changes
        .filter((c) => sel.includes(c.campo))
        .map((c) => [c.campo, c.denue]),
    );
    const { error } = await sb().rpc("denue_apply_fields", {
      p_dir: dirId,
      p_denue_id: res!.found?.denue_id ?? null,
      p_fields: fields,
    });
    if (error) return notify(error.message, { type: "error" });
    notify("Actualizada con el DENUE", { type: "success" });
    setOpen(false);
    onDone();
  };
  return (
    <>
      <Button variant="outline" onClick={look}>
        <RefreshCw /> Actualizar con DENUE
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Actualizar con el DENUE (INEGI)</DialogTitle>
            <DialogDescription>
              Consulta la API oficial del DENUE y te muestra lo que cambió. Solo
              se guarda lo que marques.
            </DialogDescription>
          </DialogHeader>
          {loading && <p className="text-sm">Consultando al INEGI…</p>}
          {err && (
            <p className="text-sm font-semibold text-[var(--critical)]">
              {err}
            </p>
          )}
          {res && !res.found && (
            <p className="text-sm">
              No encontré esta empresa en el DENUE (por su clave ni por nombre
              cerca de su ubicación).
            </p>
          )}
          {res?.found && !res.changes.length && (
            <p className="text-sm">
              Encontrada en el DENUE como «{res.found.nombre}»: sus datos son
              iguales a los tuyos.
            </p>
          )}
          {res?.found && !!res.changes.length && (
            <div className="flex flex-col gap-2 text-sm">
              <span className="text-muted-foreground">
                Encontrada como «{res.found.nombre}».
              </span>
              {res.changes.map((c) => (
                <label
                  key={c.campo}
                  className="flex items-start gap-2 rounded-md border p-2"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={sel.includes(c.campo)}
                    onChange={() =>
                      setSel((s) =>
                        s.includes(c.campo)
                          ? s.filter((x) => x !== c.campo)
                          : [...s, c.campo],
                      )
                    }
                  />
                  <span>
                    <b>{LABEL[c.campo]}</b>
                    {c.editado && (
                      <span className="text-xs text-muted-foreground">
                        {" "}
                        · lo corregiste tú
                      </span>
                    )}
                    <div className="text-muted-foreground line-through">
                      {c.actual ?? "vacío"}
                    </div>
                    <div>{c.denue}</div>
                  </span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
            {!!res?.changes.length && (
              <Button disabled={!sel.length} onClick={save}>
                Guardar {sel.length} campo{sel.length === 1 ? "" : "s"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
